import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Hero } from "@/components/Hero";
import { WhyUs } from "@/components/WhyUs";
import { Packages } from "@/components/Packages";
import { HowItWorks } from "@/components/HowItWorks";
import { SocialProof } from "@/components/SocialProof";
import { P2Banner } from "@/components/P2Banner";
import { FAQ } from "@/components/FAQ";
import { ContactForm } from "@/components/ContactForm";
import { Workshops } from "@/components/Workshops";
import { Footer } from "@/components/Footer";
import { SitePopup } from "@/components/SitePopup";
import { Button } from "@/components/ui/button";
import { Mail, Phone, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { Link } from "react-router-dom";
import { heroContent, headerContent, finalCtaContent } from "@/content/homepage";
import { useGeneralSettings } from "@/hooks/useGeneralSettings";

const Index = () => {
  const showPricing = true;
  const { settings } = useGeneralSettings();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [source, setSource] = useState<string>('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [showHeaderCta, setShowHeaderCta] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const heroCtaRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    let ticking = false;
    let lastScrollY = window.scrollY;
    let lastHeroCtaBottom: number | null = null;
    
    const updateState = () => {
      setIsScrolled(lastScrollY > 100);
      
      // Show header CTA when Hero CTA is out of view
      if (lastHeroCtaBottom !== null) {
        const threshold = window.innerWidth < 768 ? 120 : 80;
        setShowHeaderCta(lastHeroCtaBottom < threshold);
      }
      ticking = false;
    };
    
    const handleScroll = () => {
      lastScrollY = window.scrollY;
      
      // Read layout properties only when needed
      if (heroCtaRef.current) {
        lastHeroCtaBottom = heroCtaRef.current.getBoundingClientRect().bottom;
      }
      
      if (!ticking) {
        requestAnimationFrame(updateState);
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial check using requestIdleCallback to avoid forced reflow during initial render
    if ('requestIdleCallback' in window) {
      (window as Window).requestIdleCallback(() => handleScroll(), { timeout: 100 });
    } else {
      setTimeout(handleScroll, 0);
    }
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleCtaClick = (packageName?: string, sourceLocation?: string) => {
    setSelectedPackage(packageName || null);
    setSource(sourceLocation || '');
    setIsFormOpen(true);
  };
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 80; // header height + some padding
      // Use offsetTop instead of getBoundingClientRect for better performance
      const offsetPosition = element.offsetTop - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileMenuOpen(false);
  };
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-background">
      <Helmet>
        <link rel="canonical" href="https://sangria.si/" />
        <meta property="og:url" content="https://sangria.si/" />
      </Helmet>
      {/* Header */}
      <header className={`${isScrolled ? 'border-b border-border/50' : 'border-b border-transparent'} bg-background/95 backdrop-blur-sm sticky top-0 z-50 transition-all duration-300`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Mobile Menu Button - Left */}
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" aria-label="Odpri meni">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64">
                <nav className="flex flex-col gap-4 mt-8">
                  <button onClick={() => scrollToSection('paketi')} className="text-left text-lg py-2 px-4 hover:bg-muted rounded-md transition-colors">
                    Ponudba
                  </button>
                  <button onClick={() => scrollToSection('delavnice')} className="text-left text-lg py-2 px-4 hover:bg-muted rounded-md transition-colors">
                    Delavnice
                  </button>
                  <div className="border-t border-border/50 my-4" />
                  <Link to="/priboljski" className="text-left text-lg py-2 px-4 hover:bg-muted rounded-md transition-colors block">
                    Priboljški
                  </Link>
                  <Link to="/blog" className="text-left text-lg py-2 px-4 hover:bg-muted rounded-md transition-colors block">
                    Blog
                  </Link>
                  <Link to="/zaposlitev" className="text-left text-lg py-2 px-4 hover:bg-muted rounded-md transition-colors block">
                    Zaposlitev
                  </Link>
                </nav>
              </SheetContent>
            </Sheet>

            {/* Desktop Navigation - Left */}
            <div className="hidden md:flex items-center">
              <NavigationMenu>
                <NavigationMenuList>
                  <NavigationMenuItem>
                    <NavigationMenuLink href="#paketi" className={navigationMenuTriggerStyle()} onClick={(e) => { e.preventDefault(); scrollToSection('paketi'); }} style={{
                    cursor: 'pointer'
                  }}>Paketi</NavigationMenuLink>
                  </NavigationMenuItem>
                  <NavigationMenuItem>
                    <NavigationMenuLink href="#delavnice" className={navigationMenuTriggerStyle()} onClick={(e) => { e.preventDefault(); scrollToSection('delavnice'); }} style={{
                    cursor: 'pointer'
                  }}>
                      Delavnice
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>
            </div>

            {/* Logo - Center (shows on scroll) */}
            <Link to="/" onClick={scrollToTop} className={`absolute left-1/2 transform -translate-x-1/2 transition-all duration-300 cursor-pointer ${isScrolled ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'}`}>
              <img src={settings.logo_url} alt="Sangria" className="h-8 md:h-10 w-auto" width={107} height={40} />
            </Link>
            
            {/* CTA Button - Right (shows when Hero CTA is out of view) */}
            <Button 
              onClick={() => handleCtaClick(undefined, 'Header')} 
              aria-label={headerContent.ctaButton || "Kontaktirajte nas"}
              className={`bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full transition-all duration-300 h-10 w-10 p-0 md:w-auto md:px-6 ${showHeaderCta ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`}
            >
              <Mail className="h-5 w-5 text-white md:hidden" />
              <span className="hidden md:inline">{headerContent.ctaButton}</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main>
        <Hero showPricing={showPricing} onCtaClick={pkg => handleCtaClick(pkg, 'Hero')} isScrolled={isScrolled} ctaRef={heroCtaRef} onLogoClick={scrollToTop} />
        <WhyUs />
        <div id="paketi">
          <Packages showPricing={showPricing} onCtaClick={pkg => handleCtaClick(pkg, 'Paketi')} />
        </div>
        <HowItWorks />
        <Workshops onCtaClick={() => handleCtaClick(undefined, 'Delavnice')} />
        <div id="partnerji">
          <SocialProof />
        </div>
        <P2Banner onCtaClick={() => handleCtaClick(undefined, 'P2 Razpis')} />
        <FAQ />

        {/* Final CTA */}
        <section className="py-24 md:py-32 bg-muted/20">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold mb-6 text-foreground md:text-5xl">{finalCtaContent.title}</h2>
            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">{finalCtaContent.subtitle}</p>
            <Button size="lg" onClick={() => handleCtaClick(undefined, 'Končni CTA')} className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-lg hover:shadow-xl h-14 px-10 text-lg rounded-full">
              {finalCtaContent.ctaButton}
            </Button>
          </div>
        </section>
      </main>

      {/* Footer */}
      <Footer />

      {/* Popup */}
      <SitePopup onCtaClick={() => handleCtaClick(undefined, 'Popup')} />

      {/* Contact Form Dialog */}
      <ContactForm isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} isWaitlist={!showPricing} selectedPackage={selectedPackage} source={source} />
    </div>;
};
export default Index;